import { createFileRoute, Link } from '@tanstack/react-router';
import { AdminLayout } from '../components/AdminLayout.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/')({
  component: AdminDashboard,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

function AdminDashboard() {
  const overview = trpc.admin.overview.useQuery();
  const settings = trpc.settings.get.useQuery();

  const workers = trpc.workers.list.useQuery({ limit: 500 });
  const clients = trpc.clients.list.useQuery({ limit: 500 });
  const assignments = trpc.assignments.list.useQuery({ limit: 500 });
  const absences = trpc.absences.list.useQuery({ limit: 500 });
  const timesheets = trpc.timesheets.list.useQuery({ limit: 500 });

  const showKpis = settings.data?.enableAdminKpiDashboard ?? true;

  const activeWorkers = workers.data?.filter((w) => w.status === 'approved').length ?? 0;
  const activeAssignments = assignments.data?.filter((a) => a.status === 'active').length ?? 0;
  const pendingAbsences = absences.data?.filter((a) => a.status === 'requested').length ?? 0;
  const pendingTimesheets = timesheets.data?.filter(
    (t) => t.status === 'submitted' || t.status === 'client_approved' || t.status === 'disputed',
  ).length ?? 0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">Operative Übersicht &amp; KPIs</h1>
            <p className="text-[var(--muted-foreground)]">
              Mitarbeiter-Lifecycle, Disposition, Freigaben &amp; Schnellzugriffe
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/settings">
              <Button size="sm" variant="outline">⚙️ Einstellungen</Button>
            </Link>
          </div>
        </header>

        {/* Live Operative KPIs */}
        {showKpis && (
          <div className="grid gap-4 md:grid-cols-4">
            <StatCard
              label="Aktive Worker"
              value={activeWorkers}
              description={`Von ${workers.data?.length ?? 0} registrierten Workern`}
              href="/admin/workers"
            />
            <StatCard
              label="Aktive Einsätze"
              value={activeAssignments}
              description="Laufende Zuweisungen bei Kunden"
              href="/admin/assignments"
            />
            <StatCard
              label="Offene Urlaubsanträge"
              value={pendingAbsences}
              description="Warten auf Freigabe/Decision Support"
              href="/admin/absences"
            />
            <StatCard
              label="Freizugebende Zettel"
              value={pendingTimesheets}
              description="Stundenzettel zur Staff-Freigabe"
              href="/admin/timesheets"
            />
          </div>
        )}

        {/* Onboarding & System Status */}
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Onboarding &amp; Verifikation</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <StatCard
              label="Worker in Prüfung"
              value={overview.data?.workers ?? '—'}
              description="Submitted Worker-Profile"
              href="/admin/workers"
            />
            <StatCard
              label="Kunden in Prüfung"
              value={overview.data?.clients ?? '—'}
              description="Submitted Firmenprofile"
              href="/admin/clients"
            />
            <StatCard
              label="User ohne Rolle"
              value={overview.data?.users ?? '—'}
              description="Eingeloggt, aber nicht zugewiesen"
              href="/admin/users"
            />
          </div>
        </div>

        {/* Schnellzugriffe & Disponenten-Aktionen */}
        <Card className="border-[var(--border)]">
          <CardHeader>
            <CardTitle className="text-lg">⚡ Quick Actions &amp; Module</CardTitle>
            <CardDescription>Direkte Navigation zu den Kern-Surfaces</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Link to="/admin/assignments">
              <Button variant="default" size="sm">+ Einsatz anlegen / Dispo</Button>
            </Link>
            <Link to="/admin/absences">
              <Button variant="outline" size="sm">Urlaubsanträge prüfen ({pendingAbsences})</Button>
            </Link>
            <Link to="/admin/timesheets">
              <Button variant="outline" size="sm">Stundenzettel freigeben ({pendingTimesheets})</Button>
            </Link>
            <Link to="/admin/surcharge-rule-sets">
              <Button variant="outline" size="sm">Zuschlags-Regelwerke &amp; Staffeln</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

function StatCard({
  label,
  value,
  description,
  href,
}: {
  label: string;
  value: number | string;
  description: string;
  href: string;
}) {
  return (
    <Link to={href}>
      <Card className="cursor-pointer hover:border-[oklch(0.42_0.08_190)] transition-colors">
        <CardHeader>
          <CardDescription>{label}</CardDescription>
          <CardTitle className="text-4xl">{value}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-[var(--muted-foreground)]">{description}</p>
        </CardContent>
      </Card>
    </Link>
  );
}
